import { lazy } from 'react';

/**
 * Forces a hard refresh by adding a cache-busting query parameter.
 * This bypasses browser cache and CDN (Cloudflare) cache.
 */
function forceHardRefresh() {
  const url = new URL(window.location.href);
  // Add or update cache-busting parameter
  url.searchParams.set('_cb', Date.now().toString());
  // Use replace to avoid adding to browser history
  window.location.replace(url.toString());
}

/**
 * Wrapper around React.lazy that handles chunk loading failures.
 *
 * When a new version is deployed, old chunk files are deleted from the server.
 * If a user has an old version of the app loaded and tries to navigate to a
 * lazy-loaded route, the browser will fail to fetch the deleted chunk.
 *
 * This utility:
 * 1. Attempts to load the chunk normally
 * 2. On failure, checks if it's a chunk load error
 * 3. If so, and we haven't already retried, forces a hard page refresh with
 *    cache-busting to get fresh index.html and assets
 *
 * @param {Function} importFn - The dynamic import function, e.g., () => import('./MyComponent')
 * @param {string} chunkName - Optional name for logging purposes
 * @returns {React.LazyExoticComponent} A lazy component
 */
export function lazyWithRetry(importFn, chunkName = 'unknown') {
  return lazy(() =>
    importFn().catch((error) => {
      // Check if this is a chunk loading error
      const isChunkLoadError =
        error.name === 'ChunkLoadError' ||
        error.message?.includes('Loading chunk') ||
        error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Importing a module script failed') ||
        error.message?.includes('Unable to preload CSS');

      if (isChunkLoadError) {
        // Check if we've already tried to reload for this session
        const hasReloaded = sessionStorage.getItem('chunk_reload_attempted');

        if (!hasReloaded) {
          // Mark that we're attempting a reload
          sessionStorage.setItem('chunk_reload_attempted', 'true');

          console.info(
            `[LazyWithRetry] Chunk load failed for "${chunkName}". ` +
            'Forcing hard refresh to get fresh assets...'
          );

          // Force hard refresh with cache-busting to bypass Cloudflare and browser cache
          forceHardRefresh();

          // Return a never-resolving promise to prevent further rendering
          return new Promise(() => {});
        } else {
          // We've already tried reloading, clear the flag and let the error propagate
          sessionStorage.removeItem('chunk_reload_attempted');
          console.error(
            `[LazyWithRetry] Chunk load failed for "${chunkName}" even after reload. ` +
            'This may indicate a configuration issue.'
          );
        }
      }

      // Re-throw the error for non-chunk errors or if reload already attempted
      throw error;
    })
  );
}

/**
 * Clears the chunk reload flag.
 * Call this on successful app load to reset the retry mechanism.
 */
export function clearChunkReloadFlag() {
  sessionStorage.removeItem('chunk_reload_attempted');
}
